import React, { useState, useEffect } from 'react';
import { Layout, Typography, Card, Button, Spin, Empty } from 'antd';
import { CalendarOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { getActivePressReleases } from '../api/pressReleaseApi';
import moment from 'moment';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

const PressRelease = () => {
  const [hoveredCardId, setHoveredCardId] = useState(null);
  const [pressReleases, setPressReleases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPressReleases();
  }, []);

  const fetchPressReleases = async () => {
    try {
      setLoading(true);
      const data = await getActivePressReleases();
      console.log('Fetched press releases:', data);
      setPressReleases(data);
    } catch (error) {
      console.error('Error fetching press releases:', error);
      setPressReleases([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString).format('MMMM DD, YYYY');
  };

  const getImageUrl = (imageUrl) => {
    console.log('Original imageUrl:', imageUrl);
    if (!imageUrl) return '';
    if (imageUrl.startsWith('http')) return imageUrl;
    // Use relative path to work with proxy
    console.log('Final image URL:', imageUrl);
    return imageUrl;
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#fafbfc', fontFamily: "'Rubik', sans-serif" }}>
      <Content style={{ padding: '50px 48px 80px' }}>
        <div style={{ maxWidth: 1400, margin: '0 auto' }}>
          
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <Title level={1} style={{
              margin: 0,
              color: '#000000',
              fontFamily: "'Rubik', sans-serif",
              fontSize: 46,
              fontWeight: 600,
              marginBottom: 24,
              letterSpacing: '-0.5px'
            }}>
              Press Releases
            </Title>
            <Text style={{ 
              fontSize: 18, 
              color: '#666', 
              fontFamily: "'Rubik', sans-serif",
              lineHeight: '1.8',
              fontWeight: 400,
              maxWidth: 850,
              margin: '0 auto',
              display: 'block'
            }}>
              Stay updated with the latest news and announcements from Vidarbha's industrial landscape
            </Text>
          </div>

          {/* Press Release Cards */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <Spin size="large" />
              <p style={{ marginTop: 24, color: '#666', fontFamily: "'Rubik', sans-serif", fontSize: 16 }}>Loading press releases...</p>
            </div>
          ) : pressReleases.length === 0 ? (
            <Card bordered={false} style={{ background: '#fff', borderRadius: 12, border: '1px solid #eee', padding: '60px 0' }}>
              <Empty
                description={
                  <span style={{ fontFamily: "'Rubik', sans-serif", fontSize: 18, fontWeight: 600, color: '#470172' }}>
                    No press releases available. Check back later for updates.
                  </span>
                }
              />
            </Card>
          ) : (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(600px, 1fr))',
              gap: 40 
            }}>
              {pressReleases.map((release) => (
                <Card
                  key={release._id}
                  bordered={false}
                  style={{
                    background: '#fff',
                    borderRadius: 12,
                    boxShadow: hoveredCardId === release._id ? '0 20px 40px rgba(0,0,0,0.12)' : '0 4px 12px rgba(0,0,0,0.08)',
                    border: '1px solid #eee',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    transform: hoveredCardId === release._id ? 'translateY(-4px)' : 'translateY(0)'
                  }}
                  bodyStyle={{ padding: 0 }}
                  onMouseEnter={() => setHoveredCardId(release._id)}
                  onMouseLeave={() => setHoveredCardId(null)}
                >
                  <div style={{ 
                    display: 'flex',
                    flexDirection: window.innerWidth < 768 ? 'column' : 'row',
                    gap: 0
                  }}>
                    {/* Image Section */}
                    <div style={{ 
                      flex: '0 0 400px',
                      maxWidth: window.innerWidth < 768 ? '100%' : '300px',
                      height: window.innerWidth < 768 ? '250px' : '300px',
                      position: 'relative',
                      overflow: 'hidden',
                      backgroundColor: '#f0f0f0'
                    }}>
                      <img 
                        src={getImageUrl(release.imageUrl)}
                        alt={release.title}
                        onError={(e) => {
                          console.error('Image failed to load:', getImageUrl(release.imageUrl));
                          e.target.style.display = 'none';
                        }}
                        onLoad={() => console.log('Image loaded successfully:', getImageUrl(release.imageUrl))}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.3s ease',
                          transform: hoveredCardId === release._id ? 'scale(1.05)' : 'scale(1)'
                        }}
                      />
                    </div>

                    {/* Content Section */}
                    <div style={{ 
                      flex: 1,
                      padding: 32,
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      {/* Newspaper Name and Date */}
                      <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      marginBottom: 16,
                      gap: 16,
                      flexWrap: 'wrap'
                    }}>
                      {/* Newspaper Name */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        color: '#470172',
                        fontSize: 14,
                        fontWeight: 600
                      }}>
                        <CalendarOutlined />
                        <span>{release.newspaper}</span>
                      </div>
                      
                      {/* Date */}
                      <div style={{
                        fontSize: 14,
                        color: '#470172',
                        fontWeight: 500
                      }}>
                        {formatDate(release.date)}
                      </div>
                    </div>

                    {/* Title */}
                    <Title level={3} style={{
                      fontFamily: "'Rubik', sans-serif",
                      fontSize: 20,
                      fontWeight: 600,
                      color: hoveredCardId === release._id ? '#470172' : '#2d3748',
                      marginBottom: 16,
                      lineHeight: 1.4,
                      transition: 'color 0.3s ease',
                      margin: 0,
                      marginBottom: 16
                    }}>
                      {release.title}
                    </Title>

                    {/* Description */}
                    <Paragraph style={{
                      fontFamily: "'Rubik', sans-serif",
                      fontSize: 15,
                      color: '#718096',
                      lineHeight: 1.7,
                      marginBottom: 24,
                      flex: 1
                    }}>
                      {release.description}
                    </Paragraph>

                    {/* Read More Button */}
                    <Button
                      type="primary"
                      size="large"
                      icon={<ArrowRightOutlined />}
                      iconPosition="end"
                      href={release.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontFamily: "'Rubik', sans-serif",
                        fontSize: 14,
                        fontWeight: 500,
                        backgroundColor: '#470172',
                        borderColor: '#470172',
                        height: 44,
                        borderRadius: 6,
                        alignSelf: 'flex-start'
                      }}
                    >
                      Read Full Article
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
            </div>
          )}

        </div>
      </Content>
    </Layout>
  );
};

export default PressRelease;